import { createContext, useContext, useCallback, useEffect, useState } from 'react'
import { supabase, isConfigured } from '../lib/supabase.js'

const SiteContext = createContext(null)

// Fallback promo used before the backend is connected or if no row exists yet.
// This is also the "reset to defaults" content and the seed for the editor.
export const DEFAULT_PROMO = {
  enabled: true,
  sponsor: 'SupplyWebsite.com',
  url: 'https://supplywebsite.com',
  eyebrow: 'Sponsored offer',
  headline: 'Save 25% on your first supply order',
  sub: 'Drop your email and we’ll send a one-time coupon for 25% off your first order at SupplyWebsite.com.',
  coupon: 'SAVE25',
  delayMs: 1200,
}

export function SiteProvider({ children }) {
  const [promo, setPromo] = useState(DEFAULT_PROMO)
  const [banners, setBanners] = useState([])
  const [loaded, setLoaded] = useState(false)

  const load = useCallback(async () => {
    if (!isConfigured) {
      setPromo(DEFAULT_PROMO)
      setBanners([])
      setLoaded(true)
      return
    }
    const [settingsRes, bannersRes] = await Promise.all([
      supabase.from('site_settings').select('value').eq('key', 'promo').maybeSingle(),
      supabase.from('banners').select('*').order('sort_order', { ascending: true }),
    ])
    if (settingsRes.data?.value) setPromo({ ...DEFAULT_PROMO, ...settingsRes.data.value })
    if (!bannersRes.error && bannersRes.data) setBanners(bannersRes.data)
    setLoaded(true)
  }, [])

  useEffect(() => { load() }, [load])

  /* ---------- promo ---------- */
  const savePromo = useCallback(async (value) => {
    const merged = { ...DEFAULT_PROMO, ...value }
    const { error } = await supabase
      .from('site_settings')
      .upsert({ key: 'promo', value: merged, updated_at: new Date().toISOString() })
    if (error) return { error: error.message }
    setPromo(merged)
    return {}
  }, [])

  /* ---------- banners ---------- */
  const addBanner = useCallback(async (b) => {
    const { data, error } = await supabase.from('banners').insert(bannerRow(b)).select().single()
    if (error) return { error: error.message }
    setBanners((list) => [...list, data].sort(bySort))
    return { record: data }
  }, [])

  const updateBanner = useCallback(async (id, b) => {
    const { data, error } = await supabase.from('banners').update(bannerRow(b)).eq('id', id).select().single()
    if (error) return { error: error.message }
    setBanners((list) => list.map((x) => (x.id === id ? data : x)).sort(bySort))
    return { record: data }
  }, [])

  const deleteBanner = useCallback(async (id) => {
    const { error } = await supabase.from('banners').delete().eq('id', id)
    if (error) return { error: error.message }
    setBanners((list) => list.filter((x) => x.id !== id))
    return {}
  }, [])

  // Upload a banner image to Supabase Storage and return its public URL.
  const uploadBannerImage = useCallback(async (file) => {
    if (!file) return { error: 'No file selected.' }
    if (file.size > 5 * 1024 * 1024) return { error: 'Image is over 5 MB — please use a smaller file.' }
    const ext = (file.name.split('.').pop() || 'jpg').toLowerCase()
    const path = `banner-${Date.now()}-${Math.random().toString(36).slice(2, 8)}.${ext}`
    const { error } = await supabase.storage.from('banners').upload(path, file, {
      cacheControl: '3600',
      upsert: false,
      contentType: file.type || undefined,
    })
    if (error) return { error: error.message }
    const { data } = supabase.storage.from('banners').getPublicUrl(path)
    return { url: data.publicUrl }
  }, [])

  const value = {
    promo,
    banners,
    activeBanners: banners.filter((b) => b.active),
    loaded,
    configured: isConfigured,
    savePromo,
    addBanner,
    updateBanner,
    deleteBanner,
    uploadBannerImage,
  }
  return <SiteContext.Provider value={value}>{children}</SiteContext.Provider>
}

const bySort = (a, b) => (a.sort_order ?? 100) - (b.sort_order ?? 100)
const bannerRow = (b) => ({
  image_url: b.imageUrl?.trim() || b.image_url?.trim() || '',
  mobile_image_url: b.mobileImageUrl?.trim() || b.mobile_image_url?.trim() || null,
  link_url: b.linkUrl?.trim() || b.link_url?.trim() || null,
  caption: b.caption?.trim() || null,
  utm: b.utm?.trim() || null,
  active: b.active ?? true,
  sort_order: Number(b.sortOrder ?? b.sort_order ?? 100) || 100,
})

export const useSite = () => useContext(SiteContext)
